import { useMutation, useQuery } from "@apollo/client";
import { useMergeLink } from "@mergeapi/react-merge-link";
import { useCallback, useEffect, useMemo, useState } from "react";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";

import {
  CreateMergeLinkTokenMutation,
  CreateMergeLinkTokenMutationVariables,
  ExchangeMergePublicTokenMutation,
  ExchangeMergePublicTokenMutationVariables,
  GetAvailableMergeIntegrationsQuery,
} from "../../../../types/graphql-schema";
import createMergeLinkTokenMutation from "../../graphql/create-merge-link-token";
import exchangeMergePublicTokenMutation from "../../graphql/exchange-merge-public-token";
import getAvailableMergeIntegrationsQuery from "../../graphql/get-available-merge-integrations";
import IntegrationCard from "./integrations-card";
import { MERGE_CATEGORY_DESCRIPTIONS } from "./integrations-constants";
import {
  AvailableMergeIntegration,
  MergeIntegration,
} from "./integrations-types";

const IntegrationsMarketMerge = ({
  organization: _organization,
  connectedMergeIntegrations,
  onRefetch,
}: {
  organization: any;
  connectedMergeIntegrations: MergeIntegration[];
  onRefetch: () => void;
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [mergeLinkToken, setMergeLinkToken] = useState<string | null>(null);
  const [endUserOriginId, setEndUserOriginId] = useState<string | null>(null);
  const [connectingIntegration, setConnectingIntegration] = useState<{
    slug: string;
    category: string;
  } | null>(null);

  // ---------------------------------------------------------------------------
  // Queries and mutations
  // ---------------------------------------------------------------------------
  const { data: availableMergeIntegrationsData } =
    useQuery<GetAvailableMergeIntegrationsQuery>(
      getAvailableMergeIntegrationsQuery,
      {
        skip: !waffle.flag_is_active("merge_dev_enabled"),
      },
    );

  const [createMergeLinkToken, { loading: mergeLinkTokenLoading }] =
    useMutation<
      CreateMergeLinkTokenMutation,
      CreateMergeLinkTokenMutationVariables
    >(createMergeLinkTokenMutation);

  const [exchangeMergePublicToken] = useMutation<
    ExchangeMergePublicTokenMutation,
    ExchangeMergePublicTokenMutationVariables
  >(exchangeMergePublicTokenMutation, {
    onCompleted: () => {
      onRefetch();
    },
  });

  // ---------------------------------------------------------------------------
  // Merge.dev link handlers
  // ---------------------------------------------------------------------------
  const handleMergeLinkSuccess = useCallback(
    async (publicToken: string) => {
      if (!endUserOriginId) {
        errorNotificationVar({
          title: "Error",
          description: "No endUserOriginId available",
        });
        return;
      }

      try {
        const result = await exchangeMergePublicToken({
          variables: {
            publicToken,
            endUserOriginId,
          },
        });

        if (result.data?.exchangeMergePublicToken?.success) {
          successNotificationVar({
            title: "Success",
            description: "Integration connected successfully",
          });
          setEndUserOriginId(null);
          setConnectingIntegration(null);
        } else {
          errorNotificationVar({
            title: "Error",
            description:
              result.data?.exchangeMergePublicToken?.error ||
              "Failed to connect integration",
          });
        }
      } catch (error) {
        errorNotificationVar({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to connect integration",
        });
        throw error;
      }
    },
    [exchangeMergePublicToken, endUserOriginId],
  );

  const { open: openMergeLink, isReady: mergeLinkReady } = useMergeLink({
    linkToken: mergeLinkToken || undefined,
    onSuccess: handleMergeLinkSuccess,
    onExit: () => {
      setMergeLinkToken(null);
      setConnectingIntegration(null);
    },
  });

  const handleConnectMergeIntegration = useCallback(
    async (integrationSlug: string, category: string) => {
      setConnectingIntegration({ slug: integrationSlug, category });
      try {
        const result = await createMergeLinkToken({
          variables: {
            integrationSlug,
            category,
          },
        });

        if (result.data?.createMergeLinkToken?.success) {
          const linkToken = result.data.createMergeLinkToken.linkToken;
          const originId = result.data.createMergeLinkToken.endUserOriginId;
          setMergeLinkToken(linkToken ?? null);
          setEndUserOriginId(originId ?? null);
        } else {
          errorNotificationVar({
            title: "Error",
            description:
              result.data?.createMergeLinkToken?.error ||
              "Failed to create link token",
          });
          setConnectingIntegration(null);
        }
      } catch (error) {
        errorNotificationVar({
          title: "Error",
          description: "Failed to create link token",
        });
        setConnectingIntegration(null);
        throw error;
      }
    },
    [createMergeLinkToken],
  );

  // Auto-open MergeLink when token is set and ready
  useEffect(() => {
    if (mergeLinkToken && mergeLinkReady) {
      openMergeLink();
    }
  }, [mergeLinkToken, mergeLinkReady, openMergeLink]);

  // ---------------------------------------------------------------------------
  // Build integration-category pairs (exclude already connected)
  // ---------------------------------------------------------------------------
  const connectedKeys = useMemo(
    () =>
      new Set(connectedMergeIntegrations.map((i) => `${i.slug}_${i.category}`)),
    [connectedMergeIntegrations],
  );

  const availableMergeIntegrationPairs = useMemo(() => {
    const pairs: Array<{
      integration: AvailableMergeIntegration;
      category: string;
    }> = [];

    (availableMergeIntegrationsData?.availableMergeIntegrations ?? [])
      .filter((i): i is NonNullable<typeof i> => i !== null)
      .forEach((integration) => {
        // Create a card for each category the integration supports
        integration.categories
          .filter((c): c is string => c !== null)
          .forEach((category) => {
            const key = `${integration.slug}_${category}`;
            if (!connectedKeys.has(key)) {
              pairs.push({ integration, category });
            }
          });
      });

    return pairs;
  }, [availableMergeIntegrationsData, connectedKeys]);

  // Early return after all hooks are called
  if (!waffle.flag_is_active("merge_dev_enabled")) {
    return null;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {availableMergeIntegrationPairs.map(({ integration, category }) => {
        const key = `${integration.slug}_${category}`;
        const categoryDescription = MERGE_CATEGORY_DESCRIPTIONS[category] || "";
        return (
          <IntegrationCard
            key={key}
            name={integration.name}
            category={category}
            logo={integration.squareImage || integration.image || ""}
            description={
              <>
                <ul className="list-disc ml-4 py-2 italic">
                  <li>{`Connect your ${integration.name} account`}</li>
                  {categoryDescription && <li>{categoryDescription}</li>}
                </ul>
              </>
            }
            isLoading={
              mergeLinkTokenLoading &&
              connectingIntegration?.slug === integration.slug &&
              connectingIntegration?.category === category
            }
            onConnect={() => {
              handleConnectMergeIntegration(integration.slug, category);
            }}
          />
        );
      })}
    </>
  );
};

export { IntegrationsMarketMerge };
